<template>
    <section class="relative overflow-hidden bg-background-light pb-12 pt-12 lg:pb-16 lg:pt-16">
        <!-- Ambient amber glow + faint grid -->
        <div class="pointer-events-none absolute inset-0" aria-hidden="true">
            <div class="absolute left-1/2 top-24 h-[380px] w-[560px] max-w-[95%] -translate-x-1/2 glow-20"></div>
            <div class="absolute -left-20 top-72 h-[360px] w-[360px] glow-10"></div>
            <div class="absolute -right-20 top-60 h-[360px] w-[360px] glow-10"></div>
            <div class="absolute inset-0 hero-grid opacity-50"></div>
        </div>

        <!-- Centered thesis -->
        <div class="relative mx-auto flex max-w-[820px] flex-col items-center gap-6 px-6 text-center">
            <h1 class="text-text-main text-[2.6rem] font-black leading-[1.03] tracking-[-0.03em] sm:text-6xl lg:text-[4.5rem]">
                {{ t('hero.lead') }}
                <span class="relative whitespace-nowrap text-primary">
                    {{ t('hero.highlight') }}
                    <svg class="absolute -bottom-2 left-0 w-full" height="12" viewBox="0 0 200 12" fill="none" preserveAspectRatio="none" aria-hidden="true">
                        <path d="M2 9C40 3 160 3 198 7" stroke="#ffae00" stroke-width="4" stroke-linecap="round" />
                    </svg>
                </span>
                {{ t('hero.tail') }}
            </h1>

            <p class="max-w-[560px] text-lg leading-relaxed text-text-muted">{{ t('hero.subtitle') }}</p>

            <div class="flex flex-col gap-3 sm:flex-row">
                <RouterLink
                    :to="getRoute('/contact')"
                    class="inline-flex h-13 items-center justify-center gap-2 rounded-xl bg-primary px-7 py-3.5 text-base font-bold text-text-main shadow-[0_8px_30px_-8px_rgba(255,174,0,0.7)] transition-transform hover:-translate-y-0.5"
                >
                    {{ t('hero.cta_primary') }}
                    <span class="material-symbols-outlined text-[20px]">arrow_forward</span>
                </RouterLink>
                <RouterLink
                    :to="getRoute('/projects')"
                    class="inline-flex h-13 items-center justify-center gap-2 rounded-xl border border-text-main/15 bg-white px-7 py-3.5 text-base font-bold text-text-main transition-colors hover:border-text-main/40"
                >
                    {{ t('hero.cta_secondary') }}
                </RouterLink>
            </div>

            <!-- Low-commitment third option for visitors who are only orienting. -->
            <RouterLink
                :to="getRoute('/#ai-scan')"
                class="group -mt-1 inline-flex items-center gap-1.5 text-sm font-bold text-text-muted underline decoration-primary decoration-2 underline-offset-4 transition-colors hover:text-text-main"
            >
                {{ t('ai_scan.hero_link') }}
                <span class="material-symbols-outlined text-[18px] transition-transform group-hover:translate-x-0.5">arrow_downward</span>
            </RouterLink>
        </div>

        <!-- Product collage -->
        <div class="relative mx-auto mt-10 w-full max-w-[1140px] px-6 lg:mt-14">
            <div class="pointer-events-none absolute left-1/2 top-10 h-[420px] w-[680px] max-w-[90%] -translate-x-1/2 glow-20" aria-hidden="true"></div>
            <img
                src="/images/hero-collage-1200.webp"
                srcset="/images/hero-collage-800.webp 800w, /images/hero-collage-1200.webp 1200w, /images/hero-collage-1600.webp 1600w"
                sizes="(max-width: 1140px) 100vw, 1100px"
                width="1672"
                height="941"
                :alt="t('hero.collage_alt')"
                class="relative w-full"
                fetchpriority="high"
                decoding="async"
            />
        </div>
    </section>
</template>

<script setup lang="ts">
import { RouterLink } from 'vue-router';
import { useI18n } from 'vue-i18n';

const { t, locale } = useI18n();

const getRoute = (path: string) => {
    const currentLocale = locale.value;
    if (path.startsWith('/')) return `/${currentLocale}${path}`.replace('//', '/');
    return path;
};
</script>

<style scoped>
.hero-grid {
    background-image:
        linear-gradient(to right, rgba(24, 22, 16, 0.04) 1px, transparent 1px),
        linear-gradient(to bottom, rgba(24, 22, 16, 0.04) 1px, transparent 1px);
    background-size: 48px 48px;
    mask-image: radial-gradient(ellipse 70% 55% at 50% 35%, black 30%, transparent 75%);
    -webkit-mask-image: radial-gradient(ellipse 70% 55% at 50% 35%, black 30%, transparent 75%);
}

.h-13 { height: 3.25rem; }

/* Soft amber glows via radial-gradient instead of filter:blur().
   Large blur() radii overflow the GPU texture limit on iOS WebKit
   (Safari + Chrome on iPhone) and render as white blocks on scroll. */
.glow-10 { background: radial-gradient(closest-side, rgba(255, 174, 0, 0.10), transparent 70%); }
.glow-20 { background: radial-gradient(closest-side, rgba(255, 174, 0, 0.20), transparent 70%); }

@media (prefers-reduced-motion: reduce) {
    .animate-ping { animation: none !important; }
}
</style>
